import { ApolloError } from "@apollo/client";
import { useViewerCapabilitiesQuery } from "generated.hooks";
import useAuth from "hooks/useAuth";
import React, { createContext, useCallback, useContext, useMemo } from "react";

/**
 * Typed constants for the capability keys the UI references. The server's
 * code-defined flag registry remains the source of truth — these are just
 * spelling helpers for call sites.
 */
export const CAPABILITY_VIDEO_FEATURES = "video_features";
/**
 * The user-facing API as a whole: API keys, OAuth applications, the consent
 * screen, the REST API and MCP. Off for a user, the settings pages and the
 * consent page answer not-found, and the API answers the same.
 */
export const CAPABILITY_REST_API = "rest_api";

interface CapabilitiesContextValue {
  /** The enabled capability keys for the signed-in user. */
  enabledCapabilities: Set<string>;
  /**
   * Whether the viewer has [key]. Unknown keys, loading, query errors, and
   * logged-out state all evaluate false — capability checks are presentation
   * hints only; the server enforces access.
   */
  hasCapability: (key: string) => boolean;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

const CapabilitiesContext = createContext<CapabilitiesContextValue | null>(
  null
);

/**
 * Fetches the viewer's feature capabilities (viewerCapabilities) once for the
 * authenticated app shell so gated components never query on their own.
 */
function CapabilitiesProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const { data, loading, error, refetch } = useViewerCapabilitiesQuery({
    skip: !isAuthenticated,
  });

  const enabledCapabilities = useMemo(() => {
    // Logged-out, loading, and error states expose no capabilities even if
    // Apollo still holds a previous result — stale data must never enable
    // gated UI. (Refetches don't flip `loading` back on without
    // notifyOnNetworkStatusChange, so gated UI doesn't flicker.)
    if (!isAuthenticated || loading || error || !data?.viewerCapabilities) {
      return new Set<string>();
    }
    return new Set(data.viewerCapabilities);
  }, [isAuthenticated, loading, error, data]);

  const hasCapability = useCallback(
    (key: string) => enabledCapabilities.has(key),
    [enabledCapabilities]
  );

  const value = useMemo(
    () => ({
      enabledCapabilities,
      hasCapability,
      loading,
      error,
      refetch: () => {
        refetch();
      },
    }),
    [enabledCapabilities, hasCapability, loading, error, refetch]
  );

  return (
    <CapabilitiesContext.Provider value={value}>
      {children}
    </CapabilitiesContext.Provider>
  );
}

function useCapabilities(): CapabilitiesContextValue {
  const context = useContext(CapabilitiesContext);
  if (!context) {
    throw new Error(
      "CapabilitiesContext must be placed within CapabilitiesProvider"
    );
  }
  return context;
}

/** Convenience hook for a single capability check. */
function useCapability(key: string): boolean {
  return useCapabilities().hasCapability(key);
}

/**
 * Test helper: wraps feature-gated components with a fixed capability set so
 * tests never hit GraphQL.
 */
function MockCapabilitiesProvider({
  enabled = [],
  children,
}: {
  enabled?: string[];
  children: React.ReactNode;
}) {
  const enabledCapabilities = useMemo(() => new Set(enabled), [enabled]);
  const value = useMemo(
    () => ({
      enabledCapabilities,
      hasCapability: (key: string) => enabledCapabilities.has(key),
      loading: false,
      error: undefined,
      refetch: () => {},
    }),
    [enabledCapabilities]
  );
  return (
    <CapabilitiesContext.Provider value={value}>
      {children}
    </CapabilitiesContext.Provider>
  );
}

export {
  CapabilitiesContext,
  CapabilitiesProvider,
  MockCapabilitiesProvider,
  useCapabilities,
  useCapability,
};
